import React, { useMemo, useState } from 'react'
import { Row, Col, Spinner, Button, Form, Container, Card } from 'react-bootstrap'
import useFetch from '../../../_fetch'
import { useAuth } from '../../../auth/auth-context'
import { formatNumberLocale, formatNumber } from '../../../_utils'
import PivotDataTable, { AnalysisPivotRow } from './pivot-table'

type GroupKey = 'page' | 'domain' | 'link' | 'campaign' | 'network'

const GROUP_OPTIONS: Array<{ value: GroupKey; label: string }> = [
    { value: 'page', label: 'Page' },
    { value: 'domain', label: 'Domain' },
    { value: 'link', label: 'Link' },
    { value: 'campaign', label: 'Campaign' },
    { value: 'network', label: 'Network' },
]

type AnalysisQueryPayload = {
    fromDate: string
    toDate: string
    grouping: {
        primary: GroupKey
        secondary: GroupKey
    }
    metrics: string[]
}

const LinkScriptAnalysis: React.FC = () => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submittedQuery, setSubmittedQuery] = useState<AnalysisQueryPayload | null>(null)
    const [rows, setRows] = useState<AnalysisPivotRow[]>([])
    const [error, setError] = useState('')

    const [fromDate, setFromDate] = useState(() => {
        const fromD = new Date()
        fromD.setDate(fromD.getDate() - 30)
        return fromD.toISOString().substring(0, 10)
    })
    const [toDate, setToDate] = useState(() => new Date().toISOString().substring(0, 10))
    const [primaryGrouping, setPrimaryGrouping] = useState<GroupKey>('page')
    const [secondaryGrouping, setSecondaryGrouping] = useState<GroupKey>('link')

    const secondaryOptions = useMemo(() => {
        return GROUP_OPTIONS.filter((option) => option.value !== primaryGrouping)
    }, [primaryGrouping])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setError('')

        const payload: AnalysisQueryPayload = {
            fromDate,
            toDate,
            grouping: {
                primary: primaryGrouping,
                secondary: secondaryGrouping,
            },
            metrics: [
                'pageViews',
                'trackedClicks',
                'totalClicks',
                'transactions',
                'commission',
            ],
        }

        setSubmittedQuery(payload)

        try {
            const finalFrom = new Date(fromDate)
            finalFrom.setHours(0, 0, 0, 0)

            const finalTo = new Date(toDate)
            finalTo.setHours(23, 59, 59, 999)

            const response = await doFetch('/byelink/analysis', {
                fromDate: finalFrom.toISOString(),
                toDate: finalTo.toISOString(),
                grouping: payload.grouping,
                metrics: payload.metrics,
            })

            if (response.status >= 400) {
                throw new Error(await response.text())
            }

            const analysisRows = await response.json()
            setRows(Array.isArray(analysisRows) ? analysisRows : [])
        } catch (err: any) {
            setRows([])
            setError(err?.message || 'Failed to fetch analysis data')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Container fluid>
            <h3 className="mb-4">Link Performance</h3>

            <Form onSubmit={handleSubmit}>
                <Row className="align-items-end mb-3">
                    <Col xs={12} sm={2}>
                        <Form.Group controlId="analysisFromDate">
                            <Form.Label>From date</Form.Label>
                            <Form.Control
                                type="date"
                                value={fromDate}
                                max={toDate}
                                required
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={2}>
                        <Form.Group controlId="analysisToDate">
                            <Form.Label>To date</Form.Label>
                            <Form.Control
                                type="date"
                                value={toDate}
                                max={new Date().toISOString().substring(0, 10)}
                                required
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={2}>
                        <Form.Group controlId="analysisPrimaryGrouping">
                            <Form.Label>Primary grouping</Form.Label>
                            <Form.Select
                                value={primaryGrouping}
                                onChange={(e) => {
                                    const nextPrimary = e.target.value as GroupKey
                                    setPrimaryGrouping(nextPrimary)
                                    if (nextPrimary === secondaryGrouping) {
                                        const fallbackSecondary = GROUP_OPTIONS.find((x) => x.value !== nextPrimary)?.value || 'link'
                                        setSecondaryGrouping(fallbackSecondary)
                                    }
                                }}
                            >
                                {GROUP_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={2}>
                        <Form.Group controlId="analysisSecondaryGrouping">
                            <Form.Label>Secondary grouping</Form.Label>
                            <Form.Select
                                value={secondaryGrouping}
                                onChange={(e) => setSecondaryGrouping(e.target.value as GroupKey)}
                            >
                                {secondaryOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm="auto">
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Submit'}
                        </Button>
                    </Col>
                </Row>
            </Form>

            <hr />

            {!!error && <div className="text-danger small mb-2">{error}</div>}

            {!isSubmitting && submittedQuery && rows.length === 0 && !error && (
                <Card className="mt-2 border-0 bg-light-subtle">
                    <Card.Body className="small text-muted">
                        No analysis rows found for the selected period and grouping.
                    </Card.Body>
                </Card>
            )}

            {rows.length > 0 && (
                <div className="mt-3">
                    <PivotDataTable
                        rows={rows}
                        currencyFormatter={(value: number) => formatNumber(value, user?.settings.currency || '')}
                        numberFormatter={(value: number) => formatNumberLocale(value, 0)}
                        primaryGroupBy={submittedQuery?.grouping.primary || 'page'}
                        secondaryGroupBy={submittedQuery?.grouping.secondary || 'link'}
                    />
                </div>
            )}
        </Container>
    )
}

export default LinkScriptAnalysis
